import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { scroller } from "react-scroll";

const COMMANDS = [
  { id: "home",     label: "go to home",     desc: "scroll to hero section",    action: () => scroller.scrollTo("hero",      { smooth: true, offset: -70, duration: 500 }) },
  { id: "about",    label: "go to about",    desc: "scroll to about section",   action: () => scroller.scrollTo("about",     { smooth: true, offset: -70, duration: 500 }) },
  { id: "projects", label: "go to projects", desc: "scroll to project section", action: () => scroller.scrollTo("portfolio", { smooth: true, offset: -70, duration: 500 }) },
  { id: "skills",   label: "go to skills",   desc: "scroll to skills section",  action: () => scroller.scrollTo("skills",    { smooth: true, offset: -70, duration: 500 }) },
  { id: "contact",  label: "go to contact",  desc: "scroll to contact section", action: () => scroller.scrollTo("contact",   { smooth: true, offset: -70, duration: 500 }) },
  { id: "linkedin", label: "open linkedin",  desc: "visit LinkedIn profile",    action: () => window.open("https://www.linkedin.com/in/petrus-ardi-tama/", "_blank") },
  { id: "email",    label: "send email",     desc: "open email client",         action: () => window.open("mailto:petrus.arditama@gmail.com") },
];

const LINES = [
  "petrus@portfolio:~$ whoami",
  "petrus-arditama",
  "petrus@portfolio:~$ cat skills.txt",
  "Go · JavaScript · TypeScript · PHP · React · PostgreSQL · Redis · Docker · AWS",
  "petrus@portfolio:~$ uptime",
  `Active since ${new Date().getFullYear() - 2024} yr(s) in tech | ${new Date().getFullYear() - 2020} yr(s) total`,
  "petrus@portfolio:~$ _",
];

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [termLines, setTermLines] = useState([]);
  const inputRef = useRef(null);

  const filtered = COMMANDS.filter((c) =>
    c.label.includes(query.toLowerCase()) || c.desc.includes(query.toLowerCase())
  );

  // Typewriter for terminal lines
  useEffect(() => {
    if (!isOpen) { setTermLines([]); return; }
    let lineIdx = 0;
    let charIdx = 0;
    setTermLines([]);
    const tick = setInterval(() => {
      const line = LINES[lineIdx];
      charIdx++;
      setTermLines((prev) => {
        const next = [...prev];
        next[lineIdx] = line.slice(0, charIdx);
        return next;
      });
      if (charIdx >= line.length) {
        lineIdx++;
        charIdx = 0;
        if (lineIdx >= LINES.length) clearInterval(tick);
      }
    }, 18);
    return () => clearInterval(tick);
  }, [isOpen]);

  const runCommand = useCallback((cmd) => {
    cmd.action();
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === "Enter" && filtered[activeIdx]) { runCommand(filtered[activeIdx]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, filtered, activeIdx, onClose, runCommand]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[201] flex items-start justify-center pt-[15vh] px-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-full max-w-xl glass-card overflow-hidden">
              {/* Header */}
              <div className="px-4 py-2 border-b border-sky-400/10 flex items-center gap-2">
                <span className="font-mono text-[10px] text-sky-400/60">petrus@portfolio</span>
                <span className="font-mono text-[10px] text-gray-600">~$</span>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="type a command..."
                  className="flex-1 bg-transparent font-mono text-sm text-white outline-none placeholder-gray-600"
                />
                <span className="font-mono text-[10px] text-gray-600 border border-gray-700 px-1.5 py-0.5">ESC</span>
              </div>

              {/* Commands */}
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0 && (
                  <p className="font-mono text-xs text-gray-500 px-4 py-3">command not found: {query}</p>
                )}
                {filtered.map((cmd, i) => (
                  <button
                    key={cmd.id}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors duration-150 ${
                      i === activeIdx ? "bg-sky-400/10 border-l-2 border-sky-400" : "border-l-2 border-transparent hover:bg-sky-400/5"
                    }`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => runCommand(cmd)}
                  >
                    <span className="font-mono text-xs text-sky-300">&gt; {cmd.label}</span>
                    <span className="font-mono text-[10px] text-gray-500">{cmd.desc}</span>
                  </button>
                ))}
              </div>

              {/* Terminal output */}
              <div className="border-t border-sky-400/10 bg-black/30 px-4 py-3 max-h-40 overflow-y-auto">
                {termLines.map((line, i) => (
                  <p key={i} className={`font-mono text-[11px] leading-relaxed ${
                    line.startsWith("petrus@") ? "text-sky-400" : "text-emerald-400/80"
                  }`}>
                    {line}{i === termLines.length - 1 && <span className="animate-blink">▌</span>}
                  </p>
                ))}
              </div>

              <div className="border-t border-sky-400/10 px-4 py-1.5 flex gap-4">
                <span className="font-mono text-[9px] text-gray-600">↑↓ navigate</span>
                <span className="font-mono text-[9px] text-gray-600">↵ select</span>
                <span className="font-mono text-[9px] text-gray-600">ESC close</span>
                <span className="font-mono text-[9px] text-sky-400/40 ml-auto">ctrl+k to reopen</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
